import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  CheckCircle2, XCircle, Clock, Zap, Trophy, 
  ArrowRight, RotateCcw, Shuffle, Target
} from 'lucide-react';

// ==================== DRAG & DROP ====================
export function DragDropGame({ items, onComplete }) {
  const [shuffledItems, setShuffledItems] = useState([]);
  const [userOrder, setUserOrder] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setShuffledItems(shuffled);
    setUserOrder(shuffled.map((_, i) => i));
  }, [items]);

  const handleDragStart = (index) => {
    setDraggedItem(index);
  };

  const handleDrop = (targetIndex) => {
    if (draggedItem === null) return;
    
    const newOrder = [...userOrder];
    const draggedIdx = userOrder.indexOf(draggedItem);
    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    setUserOrder(newOrder);
    setDraggedItem(null);
  };

  const checkAnswer = () => {
    const correct = userOrder.every((itemIdx, position) => {
      return shuffledItems[itemIdx].order === position + 1;
    });
    setIsCorrect(correct);
    setCompleted(true);
    
    // Doğruysa 1.5 saniye sonra parent'a bildir
    if (correct && onComplete) {
      setTimeout(() => {
        onComplete(true);
      }, 1500);
    }
  };

  const resetGame = () => {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setShuffledItems(shuffled);
    setUserOrder(shuffled.map((_, i) => i));
    setCompleted(false);
    setIsCorrect(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Shuffle className="w-5 h-5 text-purple-600" />
        <span className="font-bold text-gray-800">Doğru sıraya dizin</span>
      </div>

      <div className="space-y-2">
        {userOrder.map((itemIdx, position) => (
          <div
            key={itemIdx}
            draggable={!completed}
            onDragStart={() => handleDragStart(itemIdx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(position)}
            className={`p-4 rounded-xl border-2 cursor-grab active:cursor-grabbing transition-all ${
              completed
                ? shuffledItems[itemIdx].order === position + 1
                  ? 'border-green-500 bg-green-50'
                  : 'border-red-500 bg-red-50'
                : draggedItem === itemIdx
                  ? 'border-purple-500 bg-purple-50 scale-105'
                  : 'border-gray-200 hover:border-purple-300 bg-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                completed
                  ? shuffledItems[itemIdx].order === position + 1
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                  : 'bg-purple-100 text-purple-600'
              }`}>
                {position + 1}
              </span>
              <span className="flex-1 font-medium">{shuffledItems[itemIdx]?.text}</span>
              {completed && (
                shuffledItems[itemIdx].order === position + 1
                  ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                  : <XCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
          </div>
        ))}
      </div>

      {!completed ? (
        <Button onClick={checkAnswer} className="w-full bg-purple-600 hover:bg-purple-700">
          Kontrol Et
        </Button>
      ) : (
        <div className="space-y-3">
          <div className={`p-4 rounded-xl text-center ${isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
            <p className={`font-bold ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
              {isCorrect ? '🎉 Mükemmel! Doğru sıralama!' : '❌ Yanlış sıralama. Tekrar dene!'}
            </p>
            {isCorrect && <p className="text-sm text-green-600 mt-2 animate-pulse">Devam ediliyor...</p>}
          </div>
          {!isCorrect && (
            <Button onClick={resetGame} variant="outline" className="w-full">
              <RotateCcw className="w-4 h-4 mr-2" /> Tekrar Dene
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== MATCHING GAME ====================
export function MatchingGame({ pairs, onComplete }) {
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [matches, setMatches] = useState({});
  const [wrongMatch, setWrongMatch] = useState(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    setLeftItems([...pairs].sort(() => Math.random() - 0.5));
    setRightItems([...pairs].sort(() => Math.random() - 0.5));
  }, [pairs]);

  const handleLeftClick = (index) => {
    if (matches[index] !== undefined) return;
    setSelectedLeft(index);
    setWrongMatch(null);
  };

  const handleRightClick = (index) => {
    if (selectedLeft === null) return;
    
    const leftItem = leftItems[selectedLeft];
    const rightItem = rightItems[index];
    
    if (leftItem.id === rightItem.id) {
      const newMatches = { ...matches, [selectedLeft]: index };
      setMatches(newMatches);
      setSelectedLeft(null);
      
      if (Object.keys(newMatches).length === pairs.length) {
        setCompleted(true);
        // 1.5 saniye sonra parent'a bildir
        setTimeout(() => {
          if (onComplete) onComplete(true);
        }, 1500);
      }
    } else {
      setWrongMatch({ left: selectedLeft, right: index });
      setTimeout(() => {
        setWrongMatch(null);
        setSelectedLeft(null);
      }, 500);
    }
  };

  const isLeftMatched = (index) => matches[index] !== undefined;
  const isRightMatched = (index) => Object.values(matches).includes(index);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-blue-600" />
        <span className="font-bold text-gray-800">Eşleştir</span>
        <span className="text-sm text-gray-500">({Object.keys(matches).length}/{pairs.length})</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-2">
          {leftItems.map((item, index) => (
            <button
              key={`left-${index}`}
              onClick={() => handleLeftClick(index)}
              disabled={isLeftMatched(index)}
              className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                isLeftMatched(index)
                  ? 'border-green-500 bg-green-50 opacity-60'
                  : selectedLeft === index
                    ? 'border-blue-500 bg-blue-50 scale-105'
                    : wrongMatch?.left === index
                      ? 'border-red-500 bg-red-50 animate-shake'
                      : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <span className="font-medium text-sm">{item.left}</span>
            </button>
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-2">
          {rightItems.map((item, index) => (
            <button
              key={`right-${index}`}
              onClick={() => handleRightClick(index)}
              disabled={isRightMatched(index)}
              className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                isRightMatched(index)
                  ? 'border-green-500 bg-green-50 opacity-60'
                  : wrongMatch?.right === index
                    ? 'border-red-500 bg-red-50 animate-shake'
                    : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <span className="font-medium text-sm">{item.right}</span>
            </button>
          ))}
        </div>
      </div>

      {completed && (
        <div className="p-4 rounded-xl bg-green-100 text-center">
          <p className="font-bold text-green-700">🎉 Tebrikler! Tüm eşleştirmeler doğru!</p>
          <p className="text-sm text-green-600 mt-2 animate-pulse">Devam ediliyor...</p>
        </div>
      )}
    </div>
  );
}

// ==================== TIME RACE ====================
export function TimeRaceGame({ questions, timePerQuestion = 10, onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timePerQuestion);
  const [gameOver, setGameOver] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [imageError, setImageError] = useState(false);

  const nextQuestion = () => {
    setShowResult(false);
    setSelectedAnswer(null);
    setImageError(false);
    
    if (currentIndex + 1 >= questions.length) {
      setGameOver(true);
      // onComplete'i hemen çağır - parent Completed Screen'i gösterecek
      if (onComplete) {
        setTimeout(() => {
          onComplete(score);
        }, 1500); // Sonuç ekranını 1.5 saniye göster
      }
    } else {
      setCurrentIndex(prev => prev + 1);
      setTimeLeft(timePerQuestion);
    }
  };

  useEffect(() => {
    if (gameOver || showResult) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          nextQuestion();
          return timePerQuestion;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentIndex, gameOver, showResult, questions]);

  const handleAnswer = (answerIndex) => {
    if (showResult) return;
    
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    // Normalize correct answer - can be index or string
    const currentQuestion = questions[currentIndex];
    let correctIndex = currentQuestion.correct;
    if (typeof correctIndex === 'string') {
      correctIndex = currentQuestion.options.findIndex(opt => 
        opt === currentQuestion.correct || 
        opt.toLowerCase() === String(currentQuestion.correct).toLowerCase()
      );
      if (correctIndex < 0) correctIndex = 0;
    }
    
    const isCorrect = answerIndex === correctIndex;
    if (isCorrect) {
      setScore(prev => prev + Math.ceil(timeLeft * 10));
    }

    setTimeout(() => {
      nextQuestion();
    }, 1000);
  };

  if (gameOver) {
    const percentage = Math.round((score / (questions.length * timePerQuestion * 10)) * 100);
    return (
      <div className="text-center p-6">
        <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Zaman Yarışı Bitti!</h3>
        <p className="text-4xl font-bold text-blue-600 mb-2">{score} puan</p>
        <p className="text-gray-500 mb-4">Devam ediliyor...</p>
        <div className="animate-pulse">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];

  return (
    <div className="space-y-4">
      {/* Timer Bar */}
      <div className="flex items-center gap-3">
        <Clock className={`w-5 h-5 ${timeLeft <= 3 ? 'text-red-500 animate-pulse' : 'text-blue-500'}`} />
        <Progress 
          value={(timeLeft / timePerQuestion) * 100} 
          className={`flex-1 h-3 ${timeLeft <= 3 ? '[&>div]:bg-red-500' : ''}`}
        />
        <span className={`font-bold ${timeLeft <= 3 ? 'text-red-500' : 'text-gray-700'}`}>{timeLeft}s</span>
      </div>

      {/* Score & Progress */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Soru {currentIndex + 1}/{questions.length}</span>
        <span className="flex items-center gap-1 text-yellow-600 font-bold">
          <Zap className="w-4 h-4" /> {score} puan
        </span>
      </div>

      {/* Question */}
      <Card>
        <CardContent className="p-6">
          {/* Görsel varsa göster */}
          {currentQ.image && (
            <div className="flex justify-center mb-4">
              <div className="relative w-32 h-32 bg-white rounded-xl shadow-lg border-2 border-gray-100 overflow-hidden flex items-center justify-center p-2">
                {imageError ? (
                  <span className="text-4xl">✈️</span>
                ) : (
                  <img 
                    src={currentQ.image} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                )}
              </div>
            </div>
          )}
          
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{currentQ.q}</h3>
          
          <div className="grid grid-cols-2 gap-3">
            {currentQ.options.map((option, index) => {
              // Normalize correct answer for styling
              let correctIdx = currentQ.correct;
              if (typeof correctIdx === 'string') {
                correctIdx = currentQ.options.findIndex(opt => 
                  opt === currentQ.correct || opt.toLowerCase() === String(currentQ.correct).toLowerCase()
                );
                if (correctIdx < 0) correctIdx = 0;
              }
              
              return (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                disabled={showResult}
                className={`p-4 rounded-xl border-2 font-medium transition-all ${
                  showResult
                    ? index === correctIdx
                      ? 'border-green-500 bg-green-50'
                      : selectedAnswer === index
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200'
                    : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 active:scale-95'
                }`}
              >
                {option}
              </button>
            );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== TRUE/FALSE GAME ====================
export function TrueFalseGame({ statements, onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [completed, setCompleted] = useState(false);

  const handleAnswer = (answer) => {
    if (showFeedback) return;
    
    const isCorrect = answer === statements[currentIndex].isTrue;
    setLastAnswer({ answer, isCorrect });
    setShowFeedback(true);
    
    const newScore = isCorrect ? score + 1 : score;
    if (isCorrect) {
      setScore(newScore);
    }

    setTimeout(() => {
      if (currentIndex + 1 >= statements.length) {
        setCompleted(true);
        // Tamamlanma ekranı göster, 1.5 sn sonra parent'a bildir
        setTimeout(() => {
          if (onComplete) onComplete(Math.round((newScore) / statements.length * 100));
        }, 1500);
      } else {
        setCurrentIndex(prev => prev + 1);
        setShowFeedback(false);
        setLastAnswer(null);
      }
    }, 1500);
  };

  if (completed) {
    const percentage = Math.round((score / statements.length) * 100);
    return (
      <div className="text-center p-6">
        <div className="text-6xl mb-4">{percentage >= 70 ? '🎉' : '📚'}</div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          {percentage >= 70 ? 'Harika!' : 'İyi Deneme!'}
        </h3>
        <p className="text-lg text-gray-600">{score}/{statements.length} Doğru</p>
        <p className="text-3xl font-bold text-blue-600 mt-2 mb-4">%{percentage}</p>
        <p className="text-gray-500 animate-pulse">Devam ediliyor...</p>
      </div>
    );
  }

  const current = statements[currentIndex];

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-500">{currentIndex + 1}/{statements.length}</span>
        <span className="text-green-600 font-bold">{score} doğru</span>
      </div>
      <Progress value={(currentIndex / statements.length) * 100} className="h-2" />

      {/* Statement */}
      <Card>
        <CardContent className="p-6">
          <p className="text-lg font-medium text-gray-800 text-center mb-6">
            &ldquo;{current.statement}&rdquo;
          </p>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleAnswer(true)}
              disabled={showFeedback}
              className={`p-6 rounded-xl border-2 font-bold text-lg transition-all ${
                showFeedback
                  ? lastAnswer?.answer === true
                    ? lastAnswer.isCorrect
                      ? 'border-green-500 bg-green-100'
                      : 'border-red-500 bg-red-100'
                    : current.isTrue
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200'
                  : 'border-green-200 hover:border-green-500 hover:bg-green-50'
              }`}
            >
              ✓ DOĞRU
            </button>
            <button
              onClick={() => handleAnswer(false)}
              disabled={showFeedback}
              className={`p-6 rounded-xl border-2 font-bold text-lg transition-all ${
                showFeedback
                  ? lastAnswer?.answer === false
                    ? lastAnswer.isCorrect
                      ? 'border-green-500 bg-green-100'
                      : 'border-red-500 bg-red-100'
                    : !current.isTrue
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200'
                  : 'border-red-200 hover:border-red-500 hover:bg-red-50'
              }`}
            >
              ✗ YANLIŞ
            </button>
          </div>

          {showFeedback && current.explanation && (
            <div className={`mt-4 p-3 rounded-lg ${lastAnswer?.isCorrect ? 'bg-green-50' : 'bg-amber-50'}`}>
              <p className="text-sm text-gray-700">{current.explanation}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== FILL IN BLANK ====================
export function FillInBlankGame({ sentences, onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [completed, setCompleted] = useState(false);

  const handleInputChange = (index, value) => {
    setUserAnswers(prev => ({ ...prev, [index]: value }));
  };

  const checkAnswers = () => {
    setShowResult(true);
    
    setTimeout(() => {
      if (currentIndex + 1 >= sentences.length) {
        const totalCorrect = sentences.reduce((acc, s, i) => {
          return acc + (userAnswers[i]?.toLowerCase().trim() === s.answer.toLowerCase() ? 1 : 0);
        }, 0);
        setCompleted(true);
        if (onComplete) onComplete(Math.round((totalCorrect / sentences.length) * 100));
      } else {
        setCurrentIndex(prev => prev + 1);
        setShowResult(false);
      }
    }, 2000);
  };

  if (completed) {
    const correctCount = sentences.reduce((acc, s, i) => {
      return acc + (userAnswers[i]?.toLowerCase().trim() === s.answer.toLowerCase() ? 1 : 0);
    }, 0);
    
    return (
      <div className="text-center p-6">
        <div className="text-6xl mb-4">✍️</div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Tamamlandı!</h3>
        <p className="text-lg text-gray-600">{correctCount}/{sentences.length} Doğru</p>
      </div>
    );
  }

  const current = sentences[currentIndex];
  const parts = current.text.split('___');
  const userAnswer = userAnswers[currentIndex] || '';
  const isCorrect = userAnswer.toLowerCase().trim() === current.answer.toLowerCase();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-500">{currentIndex + 1}/{sentences.length}</span>
      </div>
      <Progress value={(currentIndex / sentences.length) * 100} className="h-2" />

      <Card>
        <CardContent className="p-6">
          <p className="text-lg text-gray-800 mb-4">
            {parts[0]}
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => handleInputChange(currentIndex, e.target.value)}
              disabled={showResult}
              className={`mx-2 px-3 py-1 border-b-2 outline-none text-center font-bold min-w-[120px] ${
                showResult
                  ? isCorrect
                    ? 'border-green-500 text-green-700 bg-green-50'
                    : 'border-red-500 text-red-700 bg-red-50'
                  : 'border-blue-500 focus:border-blue-600'
              }`}
              placeholder="..."
            />
            {parts[1]}
          </p>

          {showResult && !isCorrect && (
            <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded-lg">
              Doğru cevap: <strong>{current.answer}</strong>
            </p>
          )}

          {!showResult && (
            <Button 
              onClick={checkAnswers}
              disabled={!userAnswer.trim()}
              className="w-full mt-4"
            >
              Kontrol Et <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default { DragDropGame, MatchingGame, TimeRaceGame, TrueFalseGame, FillInBlankGame };
